import React, { useState, useEffect, useRef } from 'react';
import { Image, Monitor, X, Save, Trash2, ExternalLink, Loader2, Crown, RefreshCw, Calendar, LogOut, Eye, MousePointerClick } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import QuitScreenAdmin from './QuitScreenAdmin';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VIP_FREQUENCY_OPTIONS = [
  { value: 1, label: 'x1', desc: 'Normale' },
  { value: 2, label: 'x2', desc: 'Fréquente' },
  { value: 3, label: 'x3', desc: 'Très fréquente' },
];

const VipAdSlot = ({ slot, data, onSaved }) => {
  const [enabled, setEnabled] = useState(data.enabled || false);
  const [frequency, setFrequency] = useState(data.frequency || 1);
  const [linkUrl, setLinkUrl] = useState(data.link_url || '');
  const [preview, setPreview] = useState(data.image_url ? `${process.env.REACT_APP_BACKEND_URL}${data.image_url}` : null);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const inputRef = useRef(null);

  const views = data.views || 0;
  const clicks = data.clicks || 0;
  const ctr = views > 0 ? ((clicks / views) * 100).toFixed(1) : '0.0';

  const resetStats = async () => {
    if (!window.confirm(`Remettre à zéro les stats de la Pub VIP ${slot} ?`)) return;
    setResetting(true);
    try {
      await axios.post(`${API}/vip-ads/${slot}/stats/reset`);
      toast.success(`Stats VIP ${slot} réinitialisées`);
      onSaved && onSaved();
    } catch (e) {
      toast.error('Erreur lors de la réinitialisation');
    } finally { setResetting(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('enabled', enabled ? 'true' : 'false');
      fd.append('frequency', frequency);
      fd.append('link_url', linkUrl);
      if (file) fd.append('image', file);
      const res = await axios.post(`${API}/vip-ads/${slot}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Publicité VIP ${slot} sauvegardée`);
      setFile(null);
      if (res.data.image_url) setPreview(`${process.env.REACT_APP_BACKEND_URL}${res.data.image_url}`);
      onSaved && onSaved(res.data);
    } catch (e) {
      toast.error(`Erreur sauvegarde VIP ${slot}`);
    } finally { setSaving(false); }
  };

  return (
    <div className={`rounded-xl border p-4 space-y-3 ${enabled ? 'border-amber-500/40 bg-amber-500/5' : 'border-white/10 bg-white/5'}`} data-testid={`vip-ad-slot-${slot}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Crown className={`w-4 h-4 ${enabled ? 'text-amber-400' : 'text-white/40'}`} />
          <span className="text-white text-sm font-medium">Publicité VIP {slot}</span>
        </div>
        <button
          onClick={() => setEnabled(!enabled)}
          className={`relative w-11 h-6 rounded-full transition-colors ${enabled ? 'bg-amber-500' : 'bg-white/20'}`}
          data-testid={`vip-toggle-${slot}`}
        >
          <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${enabled ? 'translate-x-5' : 'translate-x-0.5'}`} style={{ left: 0 }} />
        </button>
      </div>

      <input ref={inputRef} type="file" accept="image/*" className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) { setFile(f); setPreview(URL.createObjectURL(f)); }
        }} />
      {preview ? (
        <div className="relative rounded-lg overflow-hidden bg-white/5">
          <img src={preview} alt={`VIP ${slot}`} className="w-full h-24 object-contain" />
          <button onClick={() => inputRef.current?.click()} className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center text-white text-xs">
            Changer l'image
          </button>
        </div>
      ) : (
        <button onClick={() => inputRef.current?.click()}
          className="w-full h-16 border-2 border-dashed border-white/25 rounded-lg flex items-center justify-center gap-2 text-white/50 hover:border-amber-400 hover:text-amber-400 transition-all text-xs"
          data-testid={`vip-upload-${slot}`}>
          <Image className="w-4 h-4" /> Ajouter une image
        </button>
      )}

      <Input placeholder="🔗 Lien internet (https://…) — ouvert au clic sur la pub" value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)}
        data-testid={`vip-link-input-${slot}`}
        className="bg-white/5 border-white/10 text-white placeholder:text-white/40 h-8 text-xs" />

      {/* Stats vues / clics */}
      <div className="flex items-center justify-between rounded-lg bg-white/5 border border-white/10 px-3 py-2" data-testid={`vip-stats-${slot}`}>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5" title="Nombre d'affichages au démarrage">
            <Eye className="w-3.5 h-3.5 text-sky-400" />
            <span className="text-white text-xs font-medium" data-testid={`vip-views-${slot}`}>{views}</span>
            <span className="text-white/40 text-[10px]">vues</span>
          </div>
          <div className="flex items-center gap-1.5" title="Nombre de clics sur la pub">
            <MousePointerClick className="w-3.5 h-3.5 text-emerald-400" />
            <span className="text-white text-xs font-medium" data-testid={`vip-clicks-${slot}`}>{clicks}</span>
            <span className="text-white/40 text-[10px]">clics</span>
          </div>
          <div className="flex items-center gap-1" title="Taux de clic (clics / vues)">
            <span className="text-amber-300 text-xs font-medium" data-testid={`vip-ctr-${slot}`}>{ctr}%</span>
            <span className="text-white/40 text-[10px]">CTR</span>
          </div>
        </div>
        <button onClick={resetStats} disabled={resetting}
          className="p-1.5 rounded-md text-white/40 hover:text-red-400 hover:bg-red-500/10 transition-all"
          title="Remettre les stats à zéro"
          data-testid={`vip-stats-reset-${slot}`}>
          {resetting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
        </button>
      </div>

      <div className="flex items-center justify-between gap-2">
        <div className="flex gap-1.5">
          {VIP_FREQUENCY_OPTIONS.map((opt) => (
            <button key={opt.value} onClick={() => setFrequency(opt.value)}
              title={opt.desc}
              className={`px-2.5 py-1 rounded-lg text-xs transition-all ${frequency === opt.value ? 'bg-amber-500 text-white' : 'bg-white/10 text-white/60 hover:bg-white/20'}`}
              data-testid={`vip-freq-${slot}-${opt.value}`}>
              {opt.label}
            </button>
          ))}
          <span className="text-white/40 text-[10px] self-center ml-1">{VIP_FREQUENCY_OPTIONS.find(o => o.value === frequency)?.desc}</span>
        </div>
        <Button onClick={save} disabled={saving} size="sm" className="bg-amber-600 hover:bg-amber-500 text-white h-7 text-xs" data-testid={`vip-save-${slot}`}>
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Save className="w-3 h-3 mr-1" />OK</>}
        </Button>
      </div>
    </div>
  );
};

export const AdSettings = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [adConfig, setAdConfig] = useState(null);
  const [showQuitScreenAdmin, setShowQuitScreenAdmin] = useState(false);
  
  const [adType, setAdType] = useState('none');
  const [linkUrl, setLinkUrl] = useState('');
  const [duration, setDuration] = useState(5);
  const [vipAds, setVipAds] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [bookingUrl, setBookingUrl] = useState('');
  const [bookingLabel, setBookingLabel] = useState('');
  
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      fetchAdConfig();
      fetchBookingLink();
      fetchVipAds();
      const premium = localStorage.getItem('hypnose_cle_premium');
      setIsPremium(premium === 'true');
    }
  }, [isOpen]);

  const fetchVipAds = async () => {
    try {
      const res = await axios.get(`${API}/vip-ads`);
      setVipAds(res.data || []);
    } catch (e) { setVipAds([]); }
  };

  const fetchBookingLink = async () => {
    try {
      const res = await axios.get(`${API}/booking-link`);
      setBookingUrl(res.data.url || '');
      setBookingLabel(res.data.label || '');
    } catch (e) { /* ignore */ }
  };

  const handleSaveBooking = async () => {
    try {
      const formData = new FormData();
      formData.append('url', bookingUrl);
      if (bookingLabel) formData.append('label', bookingLabel);
      await axios.put(`${API}/booking-link`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Lien de réservation mis à jour');
    } catch (e) {
      toast.error('Erreur lors de la sauvegarde du lien');
    }
  };

  const fetchAdConfig = async () => {
    try {
      const response = await axios.get(`${API}/splash-ad`);
      setAdConfig(response.data);
      // Legacy types (adsense/alternate) are retired → map to custom or none
      const rawType = response.data.type || 'none';
      const mapped = rawType === 'adsense' ? (response.data.image_url ? 'custom' : 'none')
        : rawType === 'alternate' ? 'custom'
        : rawType;
      setAdType(mapped);
      setLinkUrl(response.data.link_url || '');
      setDuration(response.data.duration || 5);
      if (response.data.image_url) {
        setPreviewUrl(response.data.image_url.startsWith('http') 
          ? response.data.image_url 
          : `${process.env.REACT_APP_BACKEND_URL}${response.data.image_url}`
        );
      }
    } catch (error) {
      console.error('Error fetching ad config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedImage(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    const formData = new FormData();
    formData.append('ad_type', adType);
    formData.append('duration', duration);

    if (adType === 'custom') {
      if (selectedImage) formData.append('image', selectedImage);
      formData.append('link_url', linkUrl);
    }

    try {
      await axios.post(`${API}/splash-ad`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Configuration publicitaire sauvegardée');
      onClose();
    } catch (error) {
      console.error('Error saving ad config:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDisable = async () => {
    try {
      await axios.delete(`${API}/splash-ad`);
      toast.success('Publicité désactivée');
      setAdType('none');
      setPreviewUrl(null);
      setSelectedImage(null);
    } catch (error) {
      toast.error('Erreur lors de la désactivation');
    }
  };

  if (!isOpen) return null;

  const showCustomSettings = adType === 'custom';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <h2 className="text-white font-heading font-semibold flex items-center gap-2">
            <Monitor className="w-5 h-5 text-amber-400" />
            Gestion des Publicités
          </h2>
          <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="close-ad-settings">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="p-8 flex justify-center">
            <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
          </div>
        ) : (
          <div className="p-4 pb-24 space-y-6">
            {/* Quit Screen Admin shortcut */}
            <button
              onClick={() => setShowQuitScreenAdmin(true)}
              className="w-full flex items-center justify-between px-4 py-3 rounded-xl border border-amber-400/30 bg-amber-500/10 hover:bg-amber-500/20 transition"
              data-testid="open-quit-screen-admin"
            >
              <div className="flex items-center gap-3">
                <LogOut className="w-5 h-5 text-amber-300" />
                <div className="text-left">
                  <p className="text-white text-sm font-medium">Écran de sortie</p>
                  <p className="text-white/55 text-xs">Personnaliser le message / photos / pub affichés au moment de quitter</p>
                </div>
              </div>
              <span className="text-amber-300 text-sm">›</span>
            </button>
            {showQuitScreenAdmin && (
              <QuitScreenAdmin onClose={() => setShowQuitScreenAdmin(false)} />
            )}
            {/* Premium Status */}
            {isPremium && (
              <div className="p-4 rounded-xl bg-gradient-to-r from-yellow-500/10 to-amber-500/10 border border-yellow-500/30">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-yellow-500 to-amber-500 flex items-center justify-center">
                      <Crown className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-yellow-400 font-medium">Mode Premium actif</p>
                      <p className="text-white/50 text-xs">Les publicités sont désactivées pour cet utilisateur</p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      if (window.confirm('Réinitialiser le statut Premium ?')) {
                        localStorage.removeItem('hypnose_cle_premium');
                        localStorage.removeItem('hypnose_cle_premium_date');
                        setIsPremium(false);
                        toast.success('Statut Premium réinitialisé');
                        window.location.reload();
                      }
                    }}
                    className="p-2 text-white/40 hover:text-white hover:bg-white/10 rounded-lg transition-all"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Ad Type Selection */}
            <div>
              <label className="text-white/60 text-xs uppercase tracking-wider block mb-3">
                Type de publicité
              </label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={() => setAdType('none')}
                  className={`p-3 rounded-xl text-center transition-all ${
                    adType === 'none' ? 'bg-amber-500/20 border-2 border-amber-500 text-amber-400' : 'bg-white/5 border-2 border-transparent text-white/60 hover:bg-white/10'
                  }`}
                  data-testid="ad-type-none"
                >
                  <X className="w-5 h-5 mx-auto mb-1" />
                  <span className="text-xs">Aucune</span>
                </button>
                <button
                  onClick={() => setAdType('custom')}
                  className={`p-3 rounded-xl text-center transition-all ${
                    adType === 'custom' ? 'bg-amber-500/20 border-2 border-amber-500 text-amber-400' : 'bg-white/5 border-2 border-transparent text-white/60 hover:bg-white/10'
                  }`}
                  data-testid="ad-type-custom"
                >
                  <Image className="w-5 h-5 mx-auto mb-1" />
                  <span className="text-xs">Ma pub</span>
                </button>
                <button
                  onClick={() => setAdType('vip')}
                  className={`p-3 rounded-xl text-center transition-all ${
                    adType === 'vip' ? 'bg-amber-500/20 border-2 border-amber-500 text-amber-400' : 'bg-white/5 border-2 border-transparent text-white/60 hover:bg-white/10'
                  }`}
                  data-testid="ad-type-vip"
                >
                  <Crown className="w-5 h-5 mx-auto mb-1" />
                  <span className="text-xs">Pub VIP</span>
                </button>
              </div>
            </div>

            {/* VIP Ads Section */}
            {adType === 'vip' && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Crown className="w-4 h-4 text-amber-400" />
                  <label className="text-white/60 text-xs uppercase tracking-wider">
                    Publicités VIP (rotation au démarrage)
                  </label>
                </div>
                <p className="text-white/40 text-xs">
                  Les pubs activées tournent à chaque ouverture de l'app. La fréquence (x1, x2, x3) donne le nombre d'apparitions dans le cycle de rotation.
                </p>
                {vipAds.map((ad) => (
                  <VipAdSlot key={ad.slot} slot={ad.slot} data={ad} onSaved={fetchVipAds} />
                ))}
                <p className="text-amber-300/70 text-xs">
                  ⚠️ N'oubliez pas de cliquer « Sauvegarder » en bas pour activer le mode Pub VIP au démarrage.
                </p>
              </div>
            )}

            {/* Duration */}
            {adType !== 'none' && (
              <div>
                <label className="text-white/60 text-xs uppercase tracking-wider block mb-2">
                  Durée d'affichage (secondes)
                </label>
                <div className="flex gap-2">
                  {[3, 5, 7, 10].map((sec) => (
                    <button
                      key={sec}
                      onClick={() => setDuration(sec)}
                      className={`px-4 py-2 rounded-lg transition-all ${
                        duration === sec ? 'bg-amber-500 text-white' : 'bg-white/10 text-white/60 hover:bg-white/20'
                      }`}
                      data-testid={`duration-${sec}`}
                    >
                      {sec}s
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Custom Ad Settings */}
            {showCustomSettings && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-1">
                  <Image className="w-4 h-4 text-amber-400" />
                  <label className="text-white/60 text-xs uppercase tracking-wider">
                    Image publicitaire
                  </label>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="hidden"
                />
                
                {previewUrl ? (
                  <div className="relative rounded-xl overflow-hidden bg-white/5">
                    <img src={previewUrl} alt="Aperçu" className="w-full h-48 object-contain" />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center text-white"
                    >
                      Changer l'image
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full h-32 border-2 border-dashed border-white/30 rounded-xl flex flex-col items-center justify-center text-white/50 hover:border-amber-400 hover:text-amber-400 transition-all"
                  >
                    <Image className="w-8 h-8 mb-2" />
                    <span>Cliquez pour ajouter une image</span>
                  </button>
                )}

                <div>
                  <label className="text-white/60 text-xs uppercase tracking-wider block mb-2">
                    Lien de destination (optionnel)
                  </label>
                  <div className="flex items-center gap-2">
                    <ExternalLink className="w-4 h-4 text-white/40" />
                    <Input
                      placeholder="https://exemple.com"
                      value={linkUrl}
                      onChange={(e) => setLinkUrl(e.target.value)}
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Booking Link */}
            <div className="space-y-3 pt-2 border-t border-white/10">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-amber-400" />
                <label className="text-white/60 text-xs uppercase tracking-wider">
                  Lien de réservation (verrouillé pour les utilisateurs)
                </label>
              </div>
              <Input
                placeholder="https://votre-site.com/reservation"
                value={bookingUrl}
                onChange={(e) => setBookingUrl(e.target.value)}
                className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
              />
              <Input
                placeholder="Texte du bouton (ex: Réserver vos séances d'hypnose)"
                value={bookingLabel}
                onChange={(e) => setBookingLabel(e.target.value)}
                className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
              />
              <Button onClick={handleSaveBooking} size="sm" className="bg-amber-600 hover:bg-amber-500 text-white">
                <Save className="w-3.5 h-3.5 mr-1.5" />
                Sauvegarder le lien
              </Button>
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-4 border-t border-white/10">
              {adType !== 'none' && adConfig?.type !== 'none' && (
                <Button onClick={handleDisable} variant="ghost" className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Désactiver
                </Button>
              )}
              <div className="flex-1" />
              <Button onClick={onClose} variant="ghost" className="text-white/60">Annuler</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-gradient-to-r from-amber-500 to-amber-600">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" />Sauvegarder</>}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdSettings;
